import React, { useEffect, useState, useRef } from "react";
import { ServicesTable } from "../../../Services/ServicesTable/ServicesTable";
import { Form, Row, Col } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import { Service, Subservice } from "../common/types";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import Select from "react-select";
import { Option } from "../Show/types";
import useListLanguageFields from "../common/api/useListLanguageFields";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import { Column } from "@components/EditableDataTable/types/column";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";

function ServiceLanguage({ servicePassed, setServicePassed, permission }) {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [selectedLanguageField, setSelectedLanguageField] = useState({
    label: "Service Description",
    value: "service-description",
  });
  const [service, setService] = useState<Service[]>([{ ...servicePassed }]);
  const [fieldOptions, setFieldOptions] = useState<Option[]>([]);
  const { fields } = useListLanguageFields();

  const getLanguageFieldValue = (item) => {
    return selectedLanguageField.value === "service-description"
      ? item[selectedLanguageField.value]
      : item.languages?.[selectedLanguageField.value];
  };

  useEffect(() => {
    if (fields?.length && !fieldOptions.length) {
      setFieldOptions([
        { label: "Service Description", value: "service-description" },
        ...fields,
      ]);
    }
  }, [fields]);

  const getColumns = (serviceType) => {
    const columns: Column[] = [
      {
        header() {
          return `${serviceType} Name`;
        },
        cell(service: Service) {
          return service.id === "noRow" ? (
            <div>{service.name}</div>
          ) : (
            <Form.Control
              type="text"
              defaultValue={service.name}
              disabled
              readOnly
              className={`${serviceType.toLowerCase()}LanguageServiceNames`}
            />
          );
        },
      },
    ];
    if (
      serviceType === "Service" ||
      (serviceType === "Subservice" && service[0]?.subservices?.length > 0)
    ) {
      columns.push({
        id: "servicelanguageitem",
        header() {
          return (
            <>
              {selectedLanguageField.label}
              <Tippy
                content={
                  <span
                    style={{
                      textTransform: "none",
                      fontWeight: "400",
                      fontFamily: "Open Sans",
                    }}
                  >
                    This content will appear in your documents, helping describe
                    the service that you are offering. You may include certain
                    project data in the description of your services. To learn
                    more{" "}
                    <a
                      href="https://support.scopestack.io/help/including-project-data-in-service-descriptions"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline", color: "white" }}
                    >
                      see our help content
                    </a>
                    .
                  </span>
                }
                interactive={true}
                placement="bottom"
                delay={[0, 200]}
              >
                <span style={{ marginLeft: 5, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faCircleInfo} />
                </span>
              </Tippy>
            </>
          );
        },
        cell(service: Service) {
          const current = getLanguageFieldValue(service);
          const field = selectedLanguageField.value;
          return (
            <MarkdownTextAreaV2
              key={`${service.id}-${selectedLanguageField.value}`}
              value={current}
              placeholder={`Enter ${selectedLanguageField.label}`}
              alwaysEdit={true}
              onChange={(markdown) => {
                if (markdown !== current) {
                  const updated =
                    field === "service-description"
                      ? { ...service, "service-description": markdown }
                      : {
                          ...service,
                          languages: {
                            ...service.languages,
                            [field]: markdown,
                          },
                        };
                  setServiceData({ ...updated, isChanged: true });
                }
              }}
            />
          );
        },
      });
    }
    return columns;
  };

  const onUpdateServices = (subservices) => {
    const updatedService = service.map((s) => {
      s.subservices = subservices;
      return s;
    });
    setService(updatedService);
    setServicePassed(updatedService[0]);
  };

  const setServiceData = (item) => {
    const services = service ? service : [];
    let newlyUpdatedService = [...services].map((obj) => {
      if (obj?.subservices && obj.subservices.length > 0) {
        obj.subservices.forEach((sub) => {
          if (sub.id == item.id) {
            const newSubservices =
              obj?.subservices?.filter(
                (subservice) => subservice.id !== item.id
              ) || [];
            obj.subservices = [...newSubservices, item];
          }
        });
      }
      if (obj.id === item.id) {
        return item;
      } else {
        return obj;
      }
    });
    setService(newlyUpdatedService);
    setServicePassed(newlyUpdatedService[0]);
  };

  return (
    <div className="serviceLanguageTab">
      <Row style={{ marginTop: "20px" }}>
        <Col>
          <FormFieldLabel label="Guidance Language" className="" />
          <Form.Control
            as="textarea"
            rows={3}
            value={service[0]?.guidance}
            onChange={(e) => {
              setService([{ ...service[0], guidance: e.target.value }]);
              setServicePassed({ ...service[0], guidance: e.target.value });
            }}
            placeholder="Enter guidance language for the service"
            disabled={permission !== "manage"}
          />
        </Col>
      </Row>
      <Row
        style={{
          alignItems: "center",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      >
        <Col style={{ marginLeft: "16px" }} sm={7}>
          <Row style={{ alignItems: "center" }}>
            <Col className="instructionalText" sm={4}>
              Show / edit content for:
            </Col>
            <Col>
              <div style={{ width: "300px", marginLeft: "-40px" }}>
                <Select
                  onChange={(e) => setSelectedLanguageField(e!)}
                  className="basic-single"
                  value={selectedLanguageField}
                  options={fieldOptions}
                />
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <ServicesTable
        servicesPassed={service}
        onUpdateServices={onUpdateServices}
        serviceColumns={getColumns("Service")}
        subserviceColumns={getColumns("Subservice")}
        addSubservice={() => null}
        deleteService={() => null}
        hasSettings={false}
        hasDragAndDrop={true}
        isCollapsible={true}
        startUncollapsed={true}
      />
    </div>
  );
}

export default ServiceLanguage;
